import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Info from 'components/shared/Info'
import Input from 'components/shared/Input'
import Link from 'components/shared/Link'
import Text from 'components/shared/Text'
import { HOME_LINK, LOGIN_LINK } from 'constants/links'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useGuestLoginMutation } from 'store/api-slice/hooks'
import { SelfcareLoginForm } from './style'
import { useState } from 'react'
import { useApp } from 'store/steps'
import { useNavigate } from 'react-router-dom'
import { useSelfcareAuth } from 'routes/selfcare/auth/useSelfcareAuth'
import { useHandleError } from 'routes/selfcare/SelfcareLogin/hooks/useHandleError'
import { LoginForm } from './types'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { SESSION_TYPE_MARCH_PROMO } from 'constants/app'
import Checkbox from 'components/shared/Checkbox'
import { ENV_LOCAL, ENV_TEST } from 'constants/system'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'

export const SelfcareLogin = () => {
    const { language } = useApp()
    const navigate = useNavigate()
    const { formatMessage } = useIntl()
    const { isMarchPromoActive } = usePromotions()
    const { checkAuthentication } = useSelfcareAuth()

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<LoginForm>()
    const { handleError, setErrorMessage, errorMessage } =
        useHandleError(setError)
    const isError = !!Object.keys(errors).length

    const [isLoading, setIsLoading] = useState(false)

    // <-- workaround for march promo, only for localhost/test
    const isTestEnvironment = [ENV_LOCAL, ENV_TEST].includes(
        process.env.REACT_APP_ENV || ''
    )
    const [isMarchPromoSession, setIsMarchPromoSession] = useState(false)
    // -->

    const [loginToSelfcare] = useGuestLoginMutation()

    const onSubmit: SubmitHandler<LoginForm> = async (data) => {
        try {
            setIsLoading(true)
            setErrorMessage(null)
            clearErrors()
            const result = await loginToSelfcare({
                _locale: language,
                createSelfcareSessionCommand: {
                    ...data,
                    type: isMarchPromoSession
                        ? SESSION_TYPE_MARCH_PROMO
                        : undefined,
                },
            })

            setIsLoading(false)

            if ('error' in result) {
                handleError(result.error)
                return
            }

            await checkAuthentication()

            navigate('/')
        } catch (error) {
            const err = error as { error: FetchBaseQueryError }
            handleError(err.error)
            setIsLoading(false)
        }
    }

    return (
        <Box mt={[4, -24]} gap={[48, 24]}>
            <Box>
                <Link
                    variant={'primary'}
                    path={HOME_LINK}
                    iconPosition="left"
                    iconRotation={180}
                    data-testid="step-button-go-back"
                >
                    {formatMessage({
                        id: 'global.btn.go_to_homepage',
                        defaultMessage: 'Przejdź na stronę główną',
                    })}
                </Link>
            </Box>
            <SelfcareLoginForm onSubmit={handleSubmit(onSubmit)}>
                <Box
                    width="100%"
                    maxWidth={498}
                    shadow="level-1"
                    borderRadius={10}
                    overflow="hidden"
                >
                    <Box
                        gap={24}
                        bgColor="grayscale_0"
                        py={[48, 56]}
                        px={[16, 64]}
                        isFullWidth
                    >
                        <Text variant="heading3">
                            <FormattedMessage
                                id="selfcare.manage_tickets"
                                defaultMessage="_"
                                values={{
                                    strong: (chunks: string[]) => (
                                        <Text
                                            as="strong"
                                            variant="heading3"
                                            color="primary_500"
                                        >
                                            {chunks}
                                        </Text>
                                    ),
                                }}
                            />
                        </Text>
                        {!!errorMessage && (
                            <Info color="error">{errorMessage}</Info>
                        )}
                        <Input
                            {...register('paymentNumber', {
                                required: true,
                            })}
                            id="paymentNumber"
                            name="paymentNumber"
                            type="text"
                            label={formatMessage({
                                id: 'selfcare.input.payment_number',
                                defaultMessage: 'Numer płatności',
                            })}
                            isError={isError}
                            isFullWidth
                            hasNoMaxWidth
                            testId="selfcare-login-payment-number"
                        />
                        <Input
                            {...register('email', { required: true })}
                            id="email"
                            name="email"
                            type="email"
                            label={formatMessage({
                                id: 'selfcare.input.email_label',
                                defaultMessage:
                                    'Adres email podany przy rezerwacji',
                            })}
                            autofill="email"
                            isError={isError}
                            isFullWidth
                            hasNoMaxWidth
                            testId="selfcare-login-email"
                        />
                        {/* <-- workaround for march promo, only for localhost/test */}
                        {isTestEnvironment && isMarchPromoActive && (
                            <Checkbox
                                isChecked={isMarchPromoSession}
                                setIsChecked={() =>
                                    setIsMarchPromoSession((prev) => !prev)
                                }
                            >
                                <Text variant="bodySmall" color="grayscale_900">
                                    March Promo Session (only localhost/test)
                                </Text>
                            </Checkbox>
                        )}
                        {/* --> */}
                        <Button
                            type="submit"
                            width="100%"
                            testId="selfcare-login-submit-button"
                            isLoading={isLoading}
                        >
                            <FormattedMessage
                                id="global.btn.continue"
                                defaultMessage="Kontynuuj"
                            />
                        </Button>
                    </Box>
                    <Box
                        bgColor="grayscale_200"
                        direction={[
                            language === 'uk' ? 'column' : 'row',
                            'row',
                        ]}
                        py={32}
                        gap={4}
                        align="center"
                        justify="center"
                        isFullWidth
                    >
                        <Text variant="bodyStrong" color="grayscale_900">
                            <FormattedMessage
                                id="selfcare.login.have_account"
                                defaultMessage="Masz konto?"
                            />
                        </Text>
                        <Link variant="secondary" path={LOGIN_LINK}>
                            <FormattedMessage
                                id="global.btn.login"
                                defaultMessage="Zaloguj się"
                            />
                        </Link>
                    </Box>
                </Box>
            </SelfcareLoginForm>
        </Box>
    )
}
